import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { AiInstructionDataTableItem } from '@kirin/api-client/contracts/ai-instructions';
import { AI_INSTRUCTION_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/ai-instructions';
import {
  NameCell,
  TypeCell,
  FileCell,
  SummaryCell,
  ProjectsCell,
  UsersCell,
  ActionsCell,
} from './CellRenderers';
import {
  TypeFilterOption,
  ProjectFilterOption,
  FilePathFilterOption,
  UserFilterOption,
} from './FilterComponents';
import {
  AI_INSTRUCTION_FILTER_URL_KEYS,
  aiInstructionTypeOptions,
} from './constants';
import {
  fetchAiInstructionFilterOptions,
  getAiInstructionFilterOptionsQueryKey,
  fetchAiInstructionUserFilterOptions,
  getAiInstructionUserFilterOptionsQueryKey,
  fetchAiInstructionProjectFilterOptions,
  getAiInstructionProjectFilterOptionsQueryKey,
} from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

export interface ColumnHandlers {
  onNameClick: (contentId: string) => void;
  onProjectsClick: (contentId: string, fileCategory: string | null) => void;
  onUsersClick: (contentId: string, fileCategory: string | null) => void;
}

/** Extra column behaviour the tab decides, not the column layer. */
export interface ColumnOptions {
  /** KIR-4699: the Skills tab owns skills once `skills_inventory` is on. */
  hideSkillType?: boolean;
}

export function createAiInstructionColumns(
  organizationId: string,
  handlers: ColumnHandlers,
  options: ColumnOptions = {}
): ColumnConfig<AiInstructionDataTableItem>[] {
  return [
    // ── NAME ─────────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'name',
      width: 260,
      renderCell: ({ row }) => (
        <NameCell row={row} onNameClick={handlers.onNameClick} />
      ),
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[AI_INSTRUCTION_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        getQueryKey: params =>
          getAiInstructionFilterOptionsQueryKey(
            organizationId,
            AI_INSTRUCTION_FILTERABLE_FIELDS.NAME,
            params
          ),
        queryFn: params =>
          fetchAiInstructionFilterOptions(
            organizationId,
            AI_INSTRUCTION_FILTERABLE_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },

    // ── TYPE ──────────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.TYPE,
      header: 'Type',
      accessor: 'fileCategory',
      width: 180,
      renderCell: ({ row }) => <TypeCell row={row} />,
      sortable: true,
      sortField: 'type',
      sortKey: 't',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[AI_INSTRUCTION_FILTERABLE_FIELDS.TYPE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: aiInstructionTypeOptions(options.hideSkillType === true),
        render: (option, isSelected) => (
          <TypeFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },

    // ── FILE ──────────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.FILE,
      header: 'File',
      accessor: 'filePath',
      width: 300,
      renderCell: ({ row }) => <FileCell row={row} />,
      sortable: true,
      sortField: 'file',
      sortKey: 'f',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[AI_INSTRUCTION_FILTERABLE_FIELDS.FILE],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        dropdown: { width: 380 },
        getQueryKey: params =>
          getAiInstructionFilterOptionsQueryKey(
            organizationId,
            AI_INSTRUCTION_FILTERABLE_FIELDS.FILE,
            params
          ),
        queryFn: params =>
          fetchAiInstructionFilterOptions(
            organizationId,
            AI_INSTRUCTION_FILTERABLE_FIELDS.FILE,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <FilePathFilterOption
            filePath={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },

    // ── SUMMARY ───────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.SUMMARY,
      header: 'Summary',
      accessor: 'summary',
      width: 340,
      renderCell: ({ row }) => <SummaryCell row={row} />,
      sortable: true,
      sortField: 'summary',
      sortKey: 's',
      filterable: true,
      dataType: ColumnDataTypes.TEXT,
      filterOperators: [
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        { value: FilterOperators.STARTS_WITH, label: 'Starts with' },
        { value: FilterOperators.ENDS_WITH, label: 'Ends with' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[
            AI_INSTRUCTION_FILTERABLE_FIELDS.SUMMARY
          ],
      },
    },

    // ── PROJECTS ──────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.PROJECTS,
      header: 'Projects',
      accessor: 'projects',
      width: 150,
      renderCell: ({ row }) => (
        <ProjectsCell row={row} onProjectsClick={handlers.onProjectsClick} />
      ),
      sortable: true,
      sortField: 'projects',
      sortKey: 'p',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[
            AI_INSTRUCTION_FILTERABLE_FIELDS.PROJECTS
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        getQueryKey: params =>
          getAiInstructionProjectFilterOptionsQueryKey(organizationId, params),
        queryFn: params =>
          fetchAiInstructionProjectFilterOptions(organizationId, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <ProjectFilterOption
            label={option.label ?? option.value}
            isSelected={isSelected}
          />
        ),
      },
    },

    // ── USERS ─────────────────────────────────────────────────────────────────
    {
      id: AI_INSTRUCTION_FILTERABLE_FIELDS.USERS,
      header: 'Users',
      accessor: 'users',
      width: 120,
      renderCell: ({ row }) => (
        <UsersCell row={row} onUsersClick={handlers.onUsersClick} />
      ),
      sortable: true,
      sortField: 'users',
      sortKey: 'u',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...EmptyOperators,
      ],
      statePersistence: {
        urlKey:
          AI_INSTRUCTION_FILTER_URL_KEYS[
            AI_INSTRUCTION_FILTERABLE_FIELDS.USERS
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 20 },
        getQueryKey: params =>
          getAiInstructionUserFilterOptionsQueryKey(organizationId, params),
        queryFn: params =>
          fetchAiInstructionUserFilterOptions(organizationId, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <UserFilterOption
            userId={option.value}
            userName={option.metadata?.name as string | null}
            userEmail={option.metadata?.email as string | null}
            isSelected={isSelected}
          />
        ),
      },
    },

    // ── ACTIONS ───────────────────────────────────────────────────────────────
    {
      id: 'actions',
      header: 'Actions',
      // Action buttons must stay fully visible.
      clipOverflow: false,
      accessor: 'contentId',
      width: 120,
      renderCell: () => <ActionsCell />,
      sortable: false,
      filterable: false,
    },
  ];
}
